import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  LocalOffer as LocalOfferIcon,
  OpenInNew as OpenInNewIcon,
  Percent as PercentIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { api, ProjectCommissionStructure } from '../../services/api';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * `commissionCorrectAsAt` arrives as a plain `YYYY-MM-DD` calendar date — parsing
 * it into a Date would drag it across a day boundary, so format the parts directly.
 */
const formatCorrectAsAt = (date: string | null): string | null => {
  if (!date) return null;
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!parts) return null;
  const month = MONTHS[Number(parts[2]) - 1];
  if (!month) return null;
  return `${parts[3]} ${month} ${parts[1]}`;
};

/** 3.8 → "3.8%", 4 → "4%", null → "—". */
const formatPercent = (value: number | null | undefined): string =>
  value == null || !Number.isFinite(value) ? '—' : `${Number(value.toFixed(2))}%`;

const hasAnyCommissionInfo = (row: ProjectCommissionStructure): boolean =>
  row.commissionDirect != null
  || row.commissionECB != null
  || row.commissionSvc != null
  || Boolean(row.commission);

/** First non-empty line of the incentive blurb — the collapsed tag preview. */
const firstLineOf = (text: string): string => {
  const line = text.split(/\r?\n/).map((l) => l.trim()).find(Boolean);
  return line ?? text.trim();
};

/** `null` = the order the Site Plans tab lists projects in. */
type SortColumn = 'projectName' | 'commissionDirect' | 'commissionECB' | 'commissionSvc' | 'commissionCorrectAsAt' | 'ecoPropMatched' | null;
type SortDirection = 'asc' | 'desc';

const CELL_BORDER = '1px solid rgba(var(--color-text-rgb), 0.1)';

interface RateCellProps {
  value: number | null;
  emphasis?: boolean;
}

interface IncentiveTagProps {
  incentive: string;
  expanded: boolean;
  onToggle: () => void;
  fullWidth?: boolean;
}

/** The "Incentive" tag — tapping it expands the full blurb below the row. */
const IncentiveTag: React.FC<IncentiveTagProps> = ({ incentive, expanded, onToggle, fullWidth }) => (
  <Tooltip title={expanded ? 'Hide details' : 'Tap to see the full incentive'}>
    <ButtonBase
      onClick={onToggle}
      aria-expanded={expanded}
      sx={{
        maxWidth: '100%',
        width: fullWidth ? '100%' : undefined,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 0.5,
        pl: 0.75,
        pr: 0.25,
        py: 0.4,
        borderRadius: 5,
        border: '1px solid',
        borderColor: expanded
          ? 'rgba(var(--color-accent-rgb), 0.55)'
          : 'rgba(var(--color-accent-rgb), 0.3)',
        bgcolor: expanded
          ? 'rgba(var(--color-accent-rgb), 0.18)'
          : 'rgba(var(--color-accent-rgb), 0.09)',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        '&:hover': { bgcolor: 'rgba(var(--color-accent-rgb), 0.2)' },
      }}
    >
      <LocalOfferIcon sx={{ fontSize: 13, color: 'var(--color-accent)', flexShrink: 0 }} />
      <Typography
        noWrap
        sx={{
          flex: 1,
          minWidth: 0,
          textAlign: 'left',
          fontSize: 12,
          fontWeight: 600,
          color: 'rgba(var(--color-text-rgb), 0.85)',
        }}
      >
        {expanded ? 'Hide' : firstLineOf(incentive)}
      </Typography>
      <ExpandMoreIcon
        sx={{
          fontSize: 16,
          flexShrink: 0,
          color: 'var(--color-accent)',
          transform: expanded ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.2s ease',
        }}
      />
    </ButtonBase>
  </Tooltip>
);

const RateCell: React.FC<RateCellProps> = ({ value, emphasis }) => (
  <TableCell
    align="center"
    sx={{
      borderBottom: 'none',
      py: 1,
      px: { xs: 0.5, sm: 1 },
      fontWeight: emphasis ? 800 : 700,
      fontSize: { xs: 13, sm: 14 },
      whiteSpace: 'nowrap',
      // Keeps the decimal points aligned down the column.
      fontVariantNumeric: 'tabular-nums',
      color: value == null
        ? 'rgba(var(--color-text-rgb), 0.3)'
        : emphasis
          ? 'var(--color-accent)'
          : 'var(--color-text-strong)',
    }}
  >
    {formatPercent(value)}
  </TableCell>
);

const MATCH_GREEN = 'rgb(94, 186, 125)';
const MATCH_RED = 'rgb(232, 108, 96)';

/**
 * EcoProp publishes `commissionDesc` as HTML. Nothing here renders third-party
 * markup — block tags become line breaks, entities are decoded via the browser's
 * own parser, and what is left is plain text laid out with `pre-wrap`.
 */
const ecoPropDescToText = (html: string): string => {
  const withBreaks = html
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\s*\/\s*(p|div|li|tr|h[1-6])\s*>/gi, '\n')
    .replace(/<\s*li[^>]*>/gi, '• ');
  const el = document.createElement('div');
  el.innerHTML = withBreaks;
  return (el.textContent ?? '')
    // `&nbsp;` decodes to U+00A0 — written as an escape so the source has no
    // invisible non-breaking space. Normalising it lets the spacer <p>s collapse.
    .replace(/\u00a0/g, ' ')
    .split('\n')
    .map((line) => line.trim())
    .filter((line, index, lines) => line !== '' || (index > 0 && lines[index - 1] !== ''))
    .join('\n')
    .trim();
};

interface CompareRowProps {
  label: string;
  ours: number | null;
  /** Absent as well as null when the session is not an admin — both read "—". */
  theirs?: number | null;
}

/** One rate, side by side. Differing values are called out in red. */
const CompareRow: React.FC<CompareRowProps> = ({ label, ours, theirs }) => {
  const differs = !(ours == null && theirs == null)
    && (ours == null || theirs == null || Math.round(ours * 100) !== Math.round(theirs * 100));

  const valueSx = {
    fontSize: 12.5,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums' as const,
    textAlign: 'right' as const,
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr auto auto',
        columnGap: 1.5,
        alignItems: 'center',
        py: 0.4,
        px: 0.75,
        borderRadius: 0.75,
        bgcolor: differs ? 'rgba(232, 108, 96, 0.14)' : 'transparent',
      }}
    >
      <Typography sx={{ fontSize: 11.5, color: 'rgba(255, 255, 255, 0.6)' }}>{label}</Typography>
      <Typography sx={{ ...valueSx, minWidth: 52, color: '#fff' }}>{formatPercent(ours)}</Typography>
      <Typography
        sx={{ ...valueSx, minWidth: 52, color: differs ? MATCH_RED : 'rgba(255, 255, 255, 0.75)' }}
      >
        {formatPercent(theirs)}
      </Typography>
    </Box>
  );
};

/** The hover / tap panel behind the Yes-No chip. */
const MatchHint: React.FC<{ row: ProjectCommissionStructure }> = ({ row }) => {
  const matched = row.ecoPropMatched === true;
  const rawDesc = row.ecoPropCommissionDesc;
  const desc = rawDesc ? ecoPropDescToText(rawDesc) : '';
  // Only claim EcoProp publishes nothing when the server actually said so. A
  // missing field means the API build is older than this dialog, and asserting
  // "no notes" there would be a lie about EcoProp.
  const emptyDescMessage = rawDesc === undefined
    ? 'Commission notes unavailable.'
    : 'EcoProp publishes no commission notes for this project.';

  return (
    <Box sx={{ py: 0.5 }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: 0.3,
          textTransform: 'uppercase',
          color: matched ? MATCH_GREEN : MATCH_RED,
          mb: 0.75,
        }}
      >
        {matched ? 'Matches EcoProp' : 'Differs from EcoProp'}
      </Typography>

      {/* Column headings for the two sides */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto',
          columnGap: 1.5,
          px: 0.75,
          pb: 0.25,
        }}
      >
        <span />
        <Typography sx={{ fontSize: 10, minWidth: 52, textAlign: 'right', letterSpacing: 0.5, color: 'rgba(255, 255, 255, 0.5)' }}>
          OURS
        </Typography>
        <Typography sx={{ fontSize: 10, minWidth: 52, textAlign: 'right', letterSpacing: 0.5, color: 'rgba(255, 255, 255, 0.5)' }}>
          ECOPROP
        </Typography>
      </Box>

      <CompareRow label="Direct" ours={row.commissionDirect} theirs={row.ecoPropDirect} />
      <CompareRow label="ECB" ours={row.commissionECB} theirs={row.ecoPropECB} />
      <CompareRow label="Svc" ours={row.commissionSvc} theirs={row.ecoPropSvc} />

      <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255, 255, 255, 0.15)' }}>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            color: 'rgba(255, 255, 255, 0.5)',
            mb: 0.5,
          }}
        >
          EcoProp commission notes
        </Typography>
        <Typography
          sx={{
            fontSize: 12,
            lineHeight: 1.55,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: desc ? 'rgba(255, 255, 255, 0.88)' : 'rgba(255, 255, 255, 0.45)',
            // Long packages scroll inside the panel instead of running off-screen.
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          {desc || emptyDescMessage}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * "Matched EcoProp" — whether the stored rates still agree with the latest
 * EcoProp snapshot. `null` means the sync has not covered this project yet.
 */
const MatchedEcoPropTag: React.FC<{ row: ProjectCommissionStructure }> = ({ row }) => {
  if (row.ecoPropMatched == null) {
    return (
      <Tooltip title="No EcoProp snapshot on file for this project yet" enterTouchDelay={0}>
        <Typography sx={{ fontSize: 13, color: 'rgba(var(--color-text-rgb), 0.3)' }}>—</Typography>
      </Tooltip>
    );
  }

  const matched = row.ecoPropMatched;
  return (
    <Tooltip
      title={<MatchHint row={row} />}
      // Opens on tap as well as hover, and stays up long enough to read.
      enterTouchDelay={0}
      leaveTouchDelay={15000}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 380,
            px: 1.5,
            py: 1,
            bgcolor: 'rgba(18, 20, 26, 0.97)',
            border: '1px solid rgba(255, 255, 255, 0.16)',
            borderRadius: 1.5,
            boxShadow: '0 8px 28px rgba(0, 0, 0, 0.55)',
          },
        },
      }}
    >
      <Chip
        size="small"
        label={matched ? 'Yes' : 'No'}
        sx={{
          height: 22,
          cursor: 'pointer',
          fontWeight: 800,
          fontSize: 11.5,
          letterSpacing: 0.3,
          color: matched ? MATCH_GREEN : MATCH_RED,
          bgcolor: matched ? 'rgba(94, 186, 125, 0.14)' : 'rgba(232, 108, 96, 0.16)',
          border: '1px solid',
          borderColor: matched ? 'rgba(94, 186, 125, 0.4)' : 'rgba(232, 108, 96, 0.45)',
        }}
      />
    </Tooltip>
  );
};

interface CommissionStructureDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Project ids in the order the Top Viewing Site Plans tab lists them. Used to
   * scope and order the rows so the dialog mirrors that tab; when empty (the
   * plans haven't loaded) every project the endpoint returns is shown.
   */
  sitePlanOrder: string[];
  /** Opens the project's Site Plans detail page (stamps `?projectId=` in the URL). */
  onOpenProject: (row: ProjectCommissionStructure) => void;
  /**
   * Admins additionally see "Matched EcoProp" — the reconciliation against the
   * latest EcoProp snapshot. COMM-STRUCTURE holders get the
   * package only, and the endpoint omits the `ecoProp*` fields for them to
   * match.
   */
  isAdmin: boolean;
}

/**
 * Commission Structure — the commission package of every project listed in Top
 * Viewing Site Plans, as a sortable table: Direct / ECB / Svc rates, the date the
 * package was last confirmed, and the free-text incentive that expands on tap.
 */
const CommissionStructureDialog: React.FC<CommissionStructureDialogProps> = ({
  open,
  onClose,
  sitePlanOrder,
  onOpenProject,
  isAdmin,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [rows, setRows] = useState<ProjectCommissionStructure[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortColumn, setSortColumn] = useState<SortColumn>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const fetchedRef = useRef(false);

  // On phones the "as at" date and the incentive tag fold into the project cell,
  // leaving just Project + the three rates so the table fits without scrolling.
  // Desktop drops to 6 without the admin-only "Matched EcoProp" column.
  const columnCount = isMobile ? 4 : (isAdmin ? 7 : 6);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setRows(await api.getProjectCommissionStructure());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch commission structure');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || fetchedRef.current) return;
    fetchedRef.current = true;
    void fetchRows();
  }, [open, fetchRows]);

  const retry = useCallback(() => {
    fetchedRef.current = true;
    void fetchRows();
  }, [fetchRows]);

  const orderIndex = useMemo(
    () => new Map(sitePlanOrder.map((projectId, index) => [projectId, index])),
    [sitePlanOrder]
  );

  const listedRows = useMemo(
    () => (orderIndex.size > 0 ? rows.filter((row) => orderIndex.has(row.projectId)) : rows),
    [rows, orderIndex]
  );

  const visibleRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = listedRows.filter((row) => {
      // Projects with no package on file at all are not worth a row here.
      if (!hasAnyCommissionInfo(row)) return false;
      if (!query) return true;
      return `${row.projectName} ${row.district ?? ''} ${row.region ?? ''}`
        .toLowerCase()
        .includes(query);
    });

    const byListedOrder = (a: ProjectCommissionStructure, b: ProjectCommissionStructure) => {
      const aIndex = orderIndex.get(a.projectId) ?? Number.MAX_SAFE_INTEGER;
      const bIndex = orderIndex.get(b.projectId) ?? Number.MAX_SAFE_INTEGER;
      return aIndex === bIndex ? a.projectName.localeCompare(b.projectName) : aIndex - bIndex;
    };

    if (!sortColumn) return [...filtered].sort(byListedOrder);

    const factor = sortDirection === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (sortColumn === 'projectName') {
        return factor * a.projectName.localeCompare(b.projectName);
      }
      if (sortColumn === 'ecoPropMatched') {
        // No → Yes, with "not checked yet" sinking to the bottom either way, so
        // one click on the column collects every mismatch at the top.
        const rank = (value: boolean | null | undefined) => (value === false ? 0 : value === true ? 1 : 2);
        const aRank = rank(a.ecoPropMatched);
        const bRank = rank(b.ecoPropMatched);
        if (aRank === 2 && bRank === 2) return a.projectName.localeCompare(b.projectName);
        if (aRank === 2) return 1;
        if (bRank === 2) return -1;
        return aRank === bRank
          ? a.projectName.localeCompare(b.projectName)
          : factor * (aRank - bRank);
      }
      if (sortColumn === 'commissionCorrectAsAt') {
        // Blanks sink to the bottom whichever way the column is sorted.
        const aDate = a.commissionCorrectAsAt;
        const bDate = b.commissionCorrectAsAt;
        if (!aDate && !bDate) return a.projectName.localeCompare(b.projectName);
        if (!aDate) return 1;
        if (!bDate) return -1;
        return aDate === bDate
          ? a.projectName.localeCompare(b.projectName)
          : factor * aDate.localeCompare(bDate);
      }
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];
      if (aValue == null && bValue == null) return a.projectName.localeCompare(b.projectName);
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      return aValue === bValue
        ? a.projectName.localeCompare(b.projectName)
        : factor * (aValue - bValue);
    });
  }, [listedRows, search, sortColumn, sortDirection, orderIndex]);

  const toggleIncentive = useCallback((projectId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  }, []);

  /** Rates and dates open on their highest/latest value first — that's what agents look for. */
  const handleSort = useCallback((column: Exclude<SortColumn, null>) => {
    setSortColumn((prevColumn) => {
      if (prevColumn === column) {
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        return prevColumn;
      }
      // Ascending on the match column puts the "No" rows first — the whole point
      // of the column is finding them.
      setSortDirection(column === 'projectName' || column === 'ecoPropMatched' ? 'asc' : 'desc');
      return column;
    });
  }, []);

  const headCellSx = {
    // Opaque and a shade lifted off the paper so the sticky header stays readable
    // as striped rows scroll underneath it.
    bgcolor: 'var(--color-surface)',
    backgroundImage: 'linear-gradient(rgba(var(--color-text-rgb), 0.07), rgba(var(--color-text-rgb), 0.07))',
    borderBottom: '1px solid rgba(var(--color-text-rgb), 0.22)',
    color: 'rgba(var(--color-text-rgb), 0.6)',
    fontWeight: 800,
    fontSize: 11,
    letterSpacing: 0.6,
    textTransform: 'uppercase' as const,
    whiteSpace: 'nowrap' as const,
    py: 1.25,
    px: { xs: 0.5, sm: 1 },
  };

  // Fixed layout takes its column widths from the header row.
  const rateHeadSx = { ...headCellSx, width: isMobile ? 64 : 88 };

  const sortLabelSx = {
    color: 'inherit',
    '&:hover': { color: 'var(--color-text-strong)' },
    '&.Mui-active': {
      color: 'var(--color-accent)',
      '& .MuiTableSortLabel-icon': { color: 'var(--color-accent)' },
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          height: isMobile ? '100%' : '90vh',
          bgcolor: 'var(--color-surface)',
          color: 'rgba(var(--color-text-rgb), 0.9)',
          boxShadow: '0 4px 24px rgba(var(--color-shadow-rgb), 0.4)',
          border: '1px solid rgba(var(--color-text-rgb), 0.08)',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1.5,
          borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)',
          color: 'var(--color-text-strong)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <PercentIcon sx={{ color: 'var(--color-accent)', mt: 0.5 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h5" fontWeight={700} sx={{ color: 'var(--color-text-strong)' }}>
              Commission Structure
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
              {loading && rows.length === 0
                ? 'Loading commission packages…'
                : `${visibleRows.length} project${visibleRows.length === 1 ? '' : 's'} · tap a project to open it`}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 1.5 }}>
          <TextField
            size="small"
            placeholder="Search by project, district or region"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: 'rgba(var(--color-text-rgb), 0.45)' }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')} aria-label="Clear search">
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          {sortColumn && (
            <Chip
              size="small"
              clickable
              variant="outlined"
              label="Listed order"
              onClick={() => setSortColumn(null)}
              sx={{
                fontWeight: 600,
                color: 'rgba(var(--color-text-rgb), 0.75)',
                borderColor: 'rgba(var(--color-text-rgb), 0.25)',
              }}
            />
          )}
        </Box>
      </DialogTitle>

      {loading && <LinearProgress />}

      <DialogContent
        sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {error && (
          <Alert
            severity="error"
            sx={{ m: 2 }}
            action={
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={retry}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {!loading && !error && visibleRows.length === 0 && (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
              {search.trim()
                ? `No project matches “${search.trim()}”.`
                : 'No commission packages available.'}
            </Typography>
          </Box>
        )}

        {visibleRows.length > 0 && (
          <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
            {/*
              `table-layout: fixed` is what keeps the incentive tag inside the
              dialog: with auto layout the column widens to fit the `noWrap` text
              and pushes the table past the paper's edge instead of truncating.
            */}
            <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, pl: { xs: 1.5, sm: 2 } }}>
                    <TableSortLabel
                      active={sortColumn === 'projectName'}
                      direction={sortColumn === 'projectName' ? sortDirection : 'asc'}
                      onClick={() => handleSort('projectName')}
                      sx={sortLabelSx}
                    >
                      Project
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={rateHeadSx}>
                    <TableSortLabel
                      active={sortColumn === 'commissionDirect'}
                      direction={sortColumn === 'commissionDirect' ? sortDirection : 'desc'}
                      onClick={() => handleSort('commissionDirect')}
                      sx={sortLabelSx}
                    >
                      Direct
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={rateHeadSx}>
                    <TableSortLabel
                      active={sortColumn === 'commissionECB'}
                      direction={sortColumn === 'commissionECB' ? sortDirection : 'desc'}
                      onClick={() => handleSort('commissionECB')}
                      sx={sortLabelSx}
                    >
                      ECB
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={rateHeadSx}>
                    <TableSortLabel
                      active={sortColumn === 'commissionSvc'}
                      direction={sortColumn === 'commissionSvc' ? sortDirection : 'desc'}
                      onClick={() => handleSort('commissionSvc')}
                      sx={sortLabelSx}
                    >
                      Svc
                    </TableSortLabel>
                  </TableCell>
                  {!isMobile && isAdmin && (
                    <TableCell align="center" sx={{ ...headCellSx, width: 132 }}>
                      <TableSortLabel
                        active={sortColumn === 'ecoPropMatched'}
                        direction={sortColumn === 'ecoPropMatched' ? sortDirection : 'asc'}
                        onClick={() => handleSort('ecoPropMatched')}
                        sx={sortLabelSx}
                      >
                        Matched EcoProp
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell align="center" sx={{ ...headCellSx, width: 128 }}>
                      <TableSortLabel
                        active={sortColumn === 'commissionCorrectAsAt'}
                        direction={sortColumn === 'commissionCorrectAsAt' ? sortDirection : 'desc'}
                        onClick={() => handleSort('commissionCorrectAsAt')}
                        sx={sortLabelSx}
                      >
                        Correct as at
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell sx={{ ...headCellSx, pr: 2, width: '34%' }}>Incentive</TableCell>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {visibleRows.map((row, index) => {
                  const expanded = expandedIds.has(row.projectId);
                  const incentive = row.commission;
                  const correctAsAt = formatCorrectAsAt(row.commissionCorrectAsAt);
                  // Banded rows carry the separation, so no per-row rules are drawn —
                  // the tint has to be strong enough to trace a row across to the
                  // Incentive column. An expanded project keeps its band across the
                  // detail row too.
                  const stripe = index % 2 === 1
                    ? 'rgba(var(--color-text-rgb), 0.12)'
                    : 'transparent';

                  return (
                    <React.Fragment key={row.projectId}>
                      <TableRow
                        sx={{
                          bgcolor: expanded ? 'rgba(var(--color-accent-rgb), 0.18)' : stripe,
                          transition: 'background-color 0.15s ease',
                          '&:hover': { bgcolor: 'rgba(var(--color-accent-rgb), 0.22)' },
                        }}
                      >
                        {/* Project — the whole name is the link to its Site Plans page */}
                        <TableCell
                          sx={{
                            borderBottom: 'none',
                            py: 1.25,
                            pl: { xs: 1.5, sm: 2 },
                            pr: 1,
                            // Marks the open project down the left edge of the table.
                            boxShadow: expanded ? 'inset 3px 0 0 var(--color-accent)' : 'none',
                          }}
                        >
                          <ButtonBase
                            onClick={() => onOpenProject(row)}
                            sx={{
                              display: 'block',
                              textAlign: 'left',
                              maxWidth: '100%',
                              borderRadius: 1,
                              '&:hover .commission-project-name': { color: 'var(--color-accent)' },
                            }}
                            aria-label={`Open ${row.projectName}`}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                              <Typography
                                className="commission-project-name"
                                sx={{
                                  minWidth: 0,
                                  fontWeight: 700,
                                  fontSize: { xs: 13, sm: 14 },
                                  lineHeight: 1.3,
                                  color: 'var(--color-text-strong)',
                                  transition: 'color 0.15s ease',
                                }}
                              >
                                {row.projectName}
                              </Typography>
                              <OpenInNewIcon
                                sx={{ fontSize: 13, color: 'rgba(var(--color-text-rgb), 0.4)', flexShrink: 0 }}
                              />
                            </Box>
                            <Typography
                              sx={{
                                fontSize: 11,
                                lineHeight: 1.4,
                                color: 'rgba(var(--color-text-rgb), 0.5)',
                              }}
                            >
                              {[row.district, row.region].filter(Boolean).join(' · ')}
                              {isMobile && correctAsAt ? ` · ${correctAsAt}` : ''}
                            </Typography>
                          </ButtonBase>
                          {/* No column room on phones — the match tag rides under the name. */}
                          {isMobile && isAdmin && row.ecoPropMatched != null && (
                            <Box sx={{ mt: 0.75 }}>
                              <MatchedEcoPropTag row={row} />
                            </Box>
                          )}
                          {isMobile && incentive && (
                            <Box sx={{ mt: 0.75 }}>
                              <IncentiveTag
                                incentive={incentive}
                                expanded={expanded}
                                onToggle={() => toggleIncentive(row.projectId)}
                                fullWidth
                              />
                            </Box>
                          )}
                        </TableCell>

                        <RateCell value={row.commissionDirect} emphasis />
                        <RateCell value={row.commissionECB} />
                        <RateCell value={row.commissionSvc} />

                        {!isMobile && isAdmin && (
                          <TableCell align="center" sx={{ borderBottom: 'none', py: 1, px: 1 }}>
                            <MatchedEcoPropTag row={row} />
                          </TableCell>
                        )}

                        {!isMobile && (
                          <TableCell
                            align="center"
                            sx={{
                              borderBottom: 'none',
                              py: 1,
                              px: 1,
                              whiteSpace: 'nowrap',
                              fontSize: 12.5,
                              color: correctAsAt
                                ? 'rgba(var(--color-text-rgb), 0.7)'
                                : 'rgba(var(--color-text-rgb), 0.3)',
                            }}
                          >
                            {correctAsAt ?? '—'}
                          </TableCell>
                        )}

                        {/* Incentive tag — tap to expand the full text underneath */}
                        {!isMobile && (
                          <TableCell sx={{ borderBottom: 'none', py: 1, pl: 1, pr: 2 }}>
                            {incentive ? (
                              <IncentiveTag
                                incentive={incentive}
                                expanded={expanded}
                                onToggle={() => toggleIncentive(row.projectId)}
                              />
                            ) : (
                              <Typography sx={{ fontSize: 13, color: 'rgba(var(--color-text-rgb), 0.3)' }}>
                                —
                              </Typography>
                            )}
                          </TableCell>
                        )}
                      </TableRow>

                      {/* Expanded incentive text — spans the full table width */}
                      {incentive && (
                        <TableRow
                          sx={{ bgcolor: expanded ? 'rgba(var(--color-accent-rgb), 0.18)' : stripe }}
                        >
                          <TableCell
                            colSpan={columnCount}
                            sx={{
                              py: 0,
                              px: { xs: 1.5, sm: 2 },
                              borderBottom: 'none',
                              boxShadow: expanded ? 'inset 3px 0 0 var(--color-accent)' : 'none',
                            }}
                          >
                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                              <Box
                                sx={{
                                  mt: 0.5,
                                  mb: 1.5,
                                  p: 1.5,
                                  borderRadius: 1.5,
                                  bgcolor: 'rgba(var(--color-shadow-rgb), 0.35)',
                                  border: CELL_BORDER,
                                }}
                              >
                                <Typography
                                  sx={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    fontSize: 13,
                                    lineHeight: 1.6,
                                    color: 'rgba(var(--color-text-rgb), 0.9)',
                                  }}
                                >
                                  {incentive}
                                </Typography>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}

                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {visibleRows.length > 0 && (
          <Box
            sx={{
              px: 2,
              py: 1,
              borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)',
              bgcolor: 'var(--color-surface)',
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: 'rgba(var(--color-text-rgb), 0.45)' }}
            >
              Commission packages are subject to change without prior notice. Please check with the
              project ICs for the latest package.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CommissionStructureDialog;
